
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfileAndEvents } from '@/hooks/useProfile';
import { Profile } from '@/lib/types/core/profile';

// Component imports
import ProfileHeader from '@/components/profile/ProfileHeader';
import ProfileContent from '@/components/profile/ProfileContent';
import ProfileInfoSidebar from '@/components/profile/ProfileInfoSidebar';
import ProfileLoading from '@/components/profile/ProfileLoading';
import ProfileError from '@/components/profile/ProfileError';
import ProfileEditForm from '@/components/profile/ProfileEditForm';

const UserProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const { profile, events, rides, rideHistory, loading, error } = useUserProfileAndEvents(id);
  const [isEditing, setIsEditing] = useState(false);
  const [localProfile, setLocalProfile] = useState<Profile | null>(null);
  
  const isOwnProfile = user?.id === id;
  
  // Update local profile when profile changes
  useEffect(() => {
    if (profile) {
      setLocalProfile(profile);
    }
  }, [profile]);
  
  const handleEditProfile = () => {
    setIsEditing(true);
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
  };
  
  const handleSaveProfile = (updatedProfile: Profile) => {
    setLocalProfile(updatedProfile);
    setIsEditing(false);
  };
  
  // Show toast error if there's an error
  useEffect(() => {
    if (error) {
      toast({
        title: "Failed to load profile",
        description: error,
        variant: "destructive"
      });
    }
  }, [error, toast]);
  
  if (loading) {
    return <ProfileLoading />;
  }
  
  if (error || !profile) {
    return <ProfileError error={error} />;
  }
  
  return (
    <div className="bg-adv-dark min-h-screen pt-28 pb-16">
      <div className="container-custom">
        <ProfileHeader 
          profile={localProfile || profile} 
          isOwnProfile={isOwnProfile} 
          onEditProfile={handleEditProfile}
        />
        
        {isEditing ? (
          <ProfileEditForm
            profile={localProfile || profile}
            onSave={handleSaveProfile}
            onCancel={handleCancelEdit}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <ProfileContent 
              profile={localProfile || profile} 
              userEvents={events} 
              userRides={rides}
              isOwnProfile={isOwnProfile} 
            />
            
            <ProfileInfoSidebar 
              profile={localProfile || profile} 
              userEvents={events} 
              userRides={rides}
              userRideHistory={rideHistory}
              isOwnProfile={isOwnProfile} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
